import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { crmApi, usersApi } from '@/lib/api'
import { getApiErrorMessage, isValidEmail, sanitizePhoneNumber } from '@/lib/utils'
import { ActivityTimeline, QuickActivityLog, TaskList, ReminderList, CreateReminderDialog } from '@/components/crm'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { CreateLeadRequest, UpdateLeadRequest, LeadSource, LeadStatus, UserListItem } from '@/types'

interface LeadFormPageProps {
  mode?: 'create' | 'edit' | 'view'
}

const NONE_VALUE = '__none__'

export default function LeadFormPage({ mode = 'create' }: LeadFormPageProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const isViewMode = mode === 'view'
  const isEditMode = mode === 'edit'

  const [formData, setFormData] = useState<CreateLeadRequest>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    website: '',
    source_id: '',
    status_id: '',
    assigned_to: '',
    notes: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    tag_ids: [],
  })

  const { data: lead, isLoading: leadLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => crmApi.getLead(id!),
    enabled: !!id && (isEditMode || isViewMode),
  })

  const { data: sources = [] } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => crmApi.getLeadSources(true),
  })

  const { data: statuses = [] } = useQuery({
    queryKey: ['lead-statuses'],
    queryFn: () => crmApi.getLeadStatuses(true),
  })

  // Tags query - will be used in future for tag selection
  useQuery({
    queryKey: ['lead-tags'],
    queryFn: () => crmApi.getLeadTags(true),
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  useEffect(() => {
    if (lead) {
      setFormData({
        first_name: lead.first_name,
        last_name: lead.last_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company_name: lead.company_name || '',
        website: lead.website || '',
        source_id: lead.source_id || '',
        status_id: lead.status_id || '',
        assigned_to: lead.assigned_to || '',
        notes: lead.notes || '',
        address_line1: lead.address_line1 || '',
        address_line2: lead.address_line2 || '',
        city: lead.city || '',
        state: lead.state || '',
        postal_code: lead.postal_code || '',
        country: lead.country || '',
        tag_ids: lead.tags?.map(tag => tag.tag_id) || [],
      })
    }
  }, [lead])

  const createMutation = useMutation({
    mutationFn: (data: CreateLeadRequest) => crmApi.createLead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success(t('messages.createSuccess'))
      navigate('/crm/leads')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('messages.createError')))
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateLeadRequest) => crmApi.updateLead(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead', id] })
      toast.success(t('messages.updateSuccess'))
      navigate('/crm/leads')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('messages.updateError')))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.first_name.trim()) {
      toast.error(t('crm.leads.firstNameRequired'))
      return
    }

    // Validate email format if provided
    if (formData.email && formData.email.trim() && !isValidEmail(formData.email)) {
      toast.error(t('crm.leads.invalidEmail', 'Please enter a valid email address'))
      return
    }

    const submitData = {
      ...formData,
      source_id: formData.source_id || undefined,
      status_id: formData.status_id || undefined,
      assigned_to: formData.assigned_to || undefined,
    }

    if (isEditMode) {
      updateMutation.mutate(submitData)
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleChange = (field: keyof CreateLeadRequest, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (leadLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const pageTitle = isViewMode
    ? t('crm.leads.viewLead')
    : isEditMode
    ? t('crm.leads.editLead')
    : t('crm.leads.addLead')

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 -mt-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/leads')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {isViewMode ? t('crm.leads.viewLeadSubtitle') : t('crm.leads.formSubtitle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 md:grid-cols-2">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.leads.basicInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('crm.leads.firstName')} *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    disabled={isViewMode}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('crm.leads.lastName')}</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('common.phone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', sanitizePhoneNumber(e.target.value))}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">{t('crm.leads.company')}</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">{t('crm.leads.website')}</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  disabled={isViewMode}
                  placeholder="https://"
                />
              </div>
            </CardContent>
          </Card>

          {/* Lead Details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.leads.leadDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="source_id">{t('crm.leads.source')}</Label>
                <Select
                  value={formData.source_id || NONE_VALUE}
                  onValueChange={(value) => handleChange('source_id', value === NONE_VALUE ? '' : value)}
                  disabled={isViewMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('crm.leads.selectSource')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t('common.none')}</SelectItem>
                    {sources.map((source: LeadSource) => (
                      <SelectItem key={source.source_id} value={source.source_id}>
                        {source.source_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status_id">{t('crm.leads.status')}</Label>
                <Select
                  value={formData.status_id || NONE_VALUE}
                  onValueChange={(value) => handleChange('status_id', value === NONE_VALUE ? '' : value)}
                  disabled={isViewMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('crm.leads.selectStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t('common.none')}</SelectItem>
                    {statuses.map((status: LeadStatus) => (
                      <SelectItem key={status.status_id} value={status.status_id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: status.status_color }}
                          />
                          {status.status_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assigned_to">{t('crm.leads.assignedTo')}</Label>
                <Select
                  value={formData.assigned_to || NONE_VALUE}
                  onValueChange={(value) => handleChange('assigned_to', value === NONE_VALUE ? '' : value)}
                  disabled={isViewMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('crm.leads.selectAssignee')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>{t('common.unassigned')}</SelectItem>
                    {users.map((user: UserListItem) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">{t('crm.leads.notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  disabled={isViewMode}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>{t('crm.leads.address')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="address_line1">{t('common.addressLine1')}</Label>
                  <Input
                    id="address_line1"
                    value={formData.address_line1}
                    onChange={(e) => handleChange('address_line1', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_line2">{t('common.addressLine2')}</Label>
                  <Input
                    id="address_line2"
                    value={formData.address_line2}
                    onChange={(e) => handleChange('address_line2', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">{t('common.city')}</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">{t('common.state')}</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">{t('common.postalCode')}</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">{t('common.country')}</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/crm/leads')}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isEditMode ? t('common.update') : t('common.save')}
            </Button>
          </div>
        )}

        {isViewMode && (
          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/crm/leads')}>
              {t('common.back')}
            </Button>
            <Button type="button" onClick={() => navigate(`/crm/leads/${id}/edit`)}>
              {t('common.edit')}
            </Button>
          </div>
        )}
      </form>

      {/* Activity Timeline - Only show in view mode */}
      {isViewMode && id && (
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {/* Main Activity Timeline */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="flex items-center gap-2">
                  {t('crm.activities', 'Activities')}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <CreateReminderDialog entityType="LEAD" entityId={id} />
                  <QuickActivityLog entityType="LEAD" entityId={id} />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <ActivityTimeline entityType="LEAD" entityId={id} showHeader={false} maxItems={10} embedded />
              </CardContent>
            </Card>
          </div>
          {/* Sidebar with Tasks and Reminders */}
          <div className="space-y-6">
            <TaskList entityType="LEAD" entityId={id} />
            <ReminderList entityType="LEAD" entityId={id} />
          </div>
        </div>
      )}
    </div>
  )
}

